import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { WorklistSearchResult } from '@/api/endpoints/worklistSearch';
import { searchWorklists } from '@/api/endpoints/worklistSearch';

export const useWorklistSearchStore = defineStore('worklistSearch', () => {
  // State
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const searchQuery = ref('');
  const searchResults = ref<WorklistSearchResult[]>([]);
  const selectedWorklist = ref<WorklistSearchResult | null>(null);
  const abortController = ref<AbortController | null>(null);

  // Computed
  const hasResults = computed(() => searchResults.value.length > 0);
  const totalCount = computed(() => searchResults.value.length);

  const groupedResults = computed(() => {
    const groups = new Map<string, WorklistSearchResult[]>();

    searchResults.value.forEach(worklist => {
      const schemaName = worklist.dbSchemaName;
      if (!groups.has(schemaName)) {
        groups.set(schemaName, []);
      }
      groups.get(schemaName)!.push(worklist);
    });

    return Array.from(groups.entries()).map(([schemaName, worklists]) => ({
      schemaName,
      worklists: worklists.toSorted((a, b) => a.itemName.localeCompare(b.itemName))
    }));
  });

  // Actions
  const performSearch = async (query: string) => {
    // Avbryt tidligere forespørsel hvis den finnes
    if (abortController.value) {
      abortController.value.abort();
    }

    // Opprett ny abort controller
    abortController.value = new AbortController();

    // Tillat søk med tom streng (for å hente første page)
    // Hvis query er tom, send request til API (backend håndterer å returnere første page)
    isLoading.value = true;
    error.value = null;

    try {
      const response = await searchWorklists(query || '', abortController.value.signal);
      searchResults.value = response.results;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Forespørsel ble avbrutt, ignorer feil
        return;
      }

      error.value = 'Kunne ikke utføre søk. Vennligst prøv igjen.';
      console.error('Feil ved worklist søk:', err);
      searchResults.value = [];
    } finally {
      isLoading.value = false;
      abortController.value = null;
    }
  };

  const clearSearch = () => {
    searchResults.value = [];
    error.value = null;
    selectedWorklist.value = null;

    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
  };

  const setSearchQuery = (query: string) => {
    searchQuery.value = query;
  };

  const selectWorklist = (worklist: WorklistSearchResult) => {
    selectedWorklist.value = worklist;
  };

  return {
    // State
    isLoading,
    error,
    searchQuery,
    searchResults,
    selectedWorklist,

    // Computed
    hasResults,
    totalCount,
    groupedResults,

    // Actions
    performSearch,
    clearSearch,
    setSearchQuery,
    selectWorklist
  };
});
